import http from 'hub-http/clients/apiClient';
import { useCallback } from 'react';
import { BASE_URI_LC } from '../../constants/ApiConstants';
import { registerMutation, registerQuery, useMutation, useQuery } from 'academy-common-lib/utils/DataFetchingClient';
import { updateCache } from '../../utils/DataFetchingClientCache';
import { parseJSONOrReturn } from '../../utils/utils';
const SET_USER_ATTRIBUTE = registerMutation({
  fieldName: 'setUserAttribute',
  args: ['key', 'value'],
  fetcher({
    key,
    value
  }) {
    return http.post(`${BASE_URI_LC}/user-attributes`, {
      data: {
        key,
        value
      }
    });
  }
});
const DELETE_USER_ATTRIBUTE = registerMutation({
  fieldName: 'deleteUserAttribute',
  args: ['key'],
  fetcher({
    key
  }) {
    return http.delete(`${BASE_URI_LC}/user-attributes/${key}`);
  }
});
const GET_USER_ATTRIBUTES = registerQuery({
  fieldName: 'userAttributes',
  args: [],
  fetcher() {
    return http.get(`${BASE_URI_LC}/user-attributes`).then(response => {
      const json = response;
      const newAttributes = {};
      for (const attribute of json) {
        /**
         * Try to parse the value as JSON. If it fails, most likely because it's a plain string, just use the raw value.
         */
        newAttributes[attribute.attributeKey] = parseJSONOrReturn(attribute.attributeValue);
      }
      return newAttributes;
    });
  }
});
export const useUserAttributes = () => {
  const {
    data: userAttributes,
    loading: userAttributesLoading,
    error: userAttributesError
  } = useQuery(GET_USER_ATTRIBUTES);
  return {
    userAttributes,
    userAttributesLoading,
    userAttributesError,
    userAttributesLoaded: !userAttributesLoading && !userAttributesError && !!userAttributes
  };
};
export const useUserAttribute = attributeKey => {
  const {
    userAttributes,
    userAttributesLoading,
    userAttributesError,
    userAttributesLoaded: userAttributeLoaded
  } = useUserAttributes();
  const [set, {
    error: setUserAttributeError
  }] = useMutation(SET_USER_ATTRIBUTE);
  const [unset, {
    error: unsetUserAttributeError
  }] = useMutation(DELETE_USER_ATTRIBUTE);
  const setUserAttribute = useCallback(rawValue => {
    const value = JSON.stringify(rawValue);
    return set({
      variables: {
        value,
        key: attributeKey
      },
      update() {
        updateCache(GET_USER_ATTRIBUTES, attributes => {
          attributes[attributeKey] = rawValue;
        }, {});
      }
    });
  }, [attributeKey, set]);
  const unsetUserAttribute = useCallback(() => {
    return unset({
      variables: {
        key: attributeKey
      },
      update() {
        updateCache(GET_USER_ATTRIBUTES, attributes => {
          delete attributes[attributeKey];
        }, {});
      }
    });
  }, [attributeKey, unset]);
  return {
    userAttribute: userAttributes ? userAttributes[attributeKey] || null : null,
    userAttributeLoading: userAttributesLoading,
    setUserAttribute,
    unsetUserAttribute,
    userAttributesError,
    setUserAttributeError,
    unsetUserAttributeError,
    userAttributeLoaded
  };
};